/**
 * jounce-weight-dose.js
 * Custom element controller for the two-step Jounce variant selector.
 *
 * Wires Step 1 (weight) + Step 2 (variant) selections into the info card.
 * Computes daily dose + pack duration from a hardcoded dose-per-weight map.
 *
 * If/when we extend this UI to Jolly Gut / No Ruff / VitamPaws, promote the
 * dose map to a per-product metafield (custom.weight_dose_map JSON).
 */

(function () {
  if (customElements.get("jounce-two-step")) return;

  // chews per day, indexed by weight key (matches data-jounce-weight values)
  const DOSE_PER_WEIGHT = {
    small: 0.5, // ≤10 kg
    medium: 1, // 10-20 kg
    large: 2, // 20-40 kg
    xlarge: 3, // >40 kg
  };

  const WEIGHT_LABEL = {
    small: "upto 10 kg",
    medium: "10–20 kg",
    large: "20–40 kg",
    xlarge: "over 40 kg",
  };

  // Recommended pack (by chew count) when each weight is selected.
  // Logic: roughly target the 4–6 week correction course at full dose,
  // then maintenance halves the burn rate.
  const RECOMMENDED_CHEWS_BY_WEIGHT = {
    small: 50,
    medium: 50,
    large: 100,
    xlarge: 200,
  };

  function formatChews(n) {
    if (n === 0.5) return "½ crunchie";
    if (n === 1) return "1 crunchie";
    return `${n} crunchies`;
  }

  function formatMoney(cents) {
    // Cents → ₹ display. Shopify prices are in subunits.
    const rupees = Math.round(cents / 100);
    return "₹" + rupees.toLocaleString("en-IN");
  }

  // Robust chew-count parse — tolerates renames like "Pack of 50 Crunchies",
  // "50-Crunchies", "Crunchies x50 - …", etc.
  function extractChewCount(variantTitle) {
    if (!variantTitle) return null;
    const title = String(variantTitle);
    // Try: digits followed (eventually) by "crunch"
    let m = title.match(/(\d{1,4})\s*[-x×*]?\s*crunch/i);
    if (m) return parseInt(m[1], 10);
    // Try: "crunchies x 50" / "crunchies 50"
    m = title.match(/crunch\w*\s*[-x×*]?\s*(\d{1,4})/i);
    if (m) return parseInt(m[1], 10);
    // Last resort: any leading number
    m = title.match(/^\s*(\d{1,4})\b/);
    if (m) return parseInt(m[1], 10);
    console.warn(
      "[jounce-two-step] could not parse chew count from variant title:",
      variantTitle,
    );
    return null;
  }

  // Friendly pack label: "100 Crunchies" extracted from any variant title.
  // Falls back to "N Crunchies" when chew count is known, or the raw title.
  function extractPackLabel(variantTitle) {
    if (!variantTitle) return "";
    const title = String(variantTitle).trim();
    // Preferred: prefix before " - " (the original convention)
    if (title.includes(" - ")) return title.split(" - ")[0].trim();
    // Fallback: reconstruct from chew count when present
    const count = extractChewCount(title);
    if (count != null) return `${count} Crunchies`;
    return title;
  }

  class JounceTwoStep extends HTMLElement {
    constructor() {
      super();
      this.selectedWeight = null;
      this.selectedVariant = null;
      this._weightWasClicked = false; // true once the user touches Step 1
      this._packWasClicked = false; // true once the user touches Step 2
      this._abort = null;
    }

    connectedCallback() {
      this._abort = new AbortController();
      this.variants = this._readVariants();
      this.selectedVariant = this._findCheckedVariant();
      this._bindWeightButtons();
      this._bindVariantChanges();
      // Apply default weight (10–20 kg — most common Indian dog) silently:
      // no change event, no override of an explicit user pack choice.
      this._selectWeight("medium", { silent: true, initial: true });
      this._render();
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      this._abort = null;
    }

    _signal() {
      return this._abort ? this._abort.signal : undefined;
    }

    _readVariants() {
      const script = this.querySelector("script#productVariants");
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        console.warn("[jounce-two-step] productVariants JSON parse failed:", e);
        return [];
      }
    }

    _findCheckedVariant() {
      const checked = this.querySelector(
        ".jounce-two-step__plan-input:checked",
      );
      if (!checked) return this.variants[0] || null;
      return (
        this.variants.find((v) => v.option1 === checked.value) ||
        this.variants[0] ||
        null
      );
    }

    _bindWeightButtons() {
      const signal = this._signal();
      this._weightButtons = this.querySelectorAll("[data-jounce-weight]");
      this._weightButtons.forEach((btn) => {
        btn.addEventListener(
          "click",
          () => {
            this._selectWeight(btn.dataset.jounceWeight, {
              userInitiated: true,
            });
          },
          { signal },
        );
      });
    }

    _bindVariantChanges() {
      const signal = this._signal();
      // The radios live inside <variant-button> which delegates change events.
      // We add our own listener (with cleanup) to drive the info card and
      // mark Step 2 as user-initiated so future weight clicks won't clobber it.
      this.querySelectorAll(".jounce-two-step__plan-input").forEach((input) => {
        input.addEventListener(
          "change",
          (e) => {
            if (!e.target.checked) return;
            // Mark user intent unless this change came from our own auto-select.
            if (!e.target.dataset.jounceAutoSelected) {
              this._packWasClicked = true;
            } else {
              delete e.target.dataset.jounceAutoSelected;
            }
            this.selectedVariant =
              this.variants.find((v) => v.option1 === e.target.value) ||
              this.selectedVariant;
            this._render();
          },
          { signal },
        );
      });
    }

    _selectWeight(key, opts) {
      opts = opts || {};
      this.selectedWeight = key;
      if (opts.userInitiated) this._weightWasClicked = true;

      const buttons =
        this._weightButtons || this.querySelectorAll("[data-jounce-weight]");
      buttons.forEach((b) => {
        const active = b.dataset.jounceWeight === key;
        b.setAttribute("aria-checked", active ? "true" : "false");
        b.classList.toggle("is-selected", active);
      });

      // Cart attribute: only write when the customer has actually picked a
      // weight, so we don't ship a stale default to Shopflo / Klaviyo.
      const hiddenAttr = this.querySelector("[data-jounce-weight-attr]");
      if (hiddenAttr) {
        hiddenAttr.value = this._weightWasClicked
          ? WEIGHT_LABEL[key] || key
          : "";
      }

      // Smart-default: auto-select recommended pack ONLY when the customer
      // has actively clicked a weight tier (userInitiated) AND hasn't already
      // chosen a pack. On initial load we never override — the Liquid-rendered
      // default respects any ?variant=… URL param, and we must not stomp on it.
      const recommendedChews = RECOMMENDED_CHEWS_BY_WEIGHT[key];
      if (recommendedChews && !this._packWasClicked && opts.userInitiated) {
        const targetVariant = this.variants.find(
          (v) => extractChewCount(v.title) === recommendedChews,
        );
        if (targetVariant) {
          const radio = this._findRadioForVariant(targetVariant);
          if (radio && !radio.checked) {
            // Tag so our own change handler doesn't treat this as user intent.
            radio.dataset.jounceAutoSelected = "1";
            radio.checked = true;
            this.selectedVariant = targetVariant;
            if (!opts.silent) {
              // Dispatch BOTH input + change — themes vary on which they listen.
              radio.dispatchEvent(new Event("input", { bubbles: true }));
              radio.dispatchEvent(new Event("change", { bubbles: true }));
            }
          } else if (radio) {
            this.selectedVariant = targetVariant;
          }
        }
      }
      this._render();
    }

    _findRadioForVariant(variant) {
      if (!variant || variant.option1 == null) return null;
      // CSS.escape avoidance: scan instead of building an attr-selector.
      const radios = this.querySelectorAll(".jounce-two-step__plan-input");
      for (const r of radios) {
        if (r.value === variant.option1) return r;
      }
      return null;
    }

    _render() {
      const card = this.querySelector("[data-jounce-info-card]");
      if (!card || !this.selectedVariant) return;

      const variant = this.selectedVariant;
      const chewCount = extractChewCount(variant.title);
      const packLabel = extractPackLabel(variant.title);
      const dosePerDay = this.selectedWeight
        ? DOSE_PER_WEIGHT[this.selectedWeight]
        : null;

      const $pack = card.querySelector('[data-jounce-info="pack"]');
      const $dose = card.querySelector('[data-jounce-info="dose"]');
      const $duration = card.querySelector('[data-jounce-info="duration"]');
      const $price = card.querySelector('[data-jounce-info="price"]');
      const $compare = card.querySelector('[data-jounce-info="compare"]');
      const $save = card.querySelector('[data-jounce-info="save"]');

      if ($pack) $pack.textContent = "Includes: " + packLabel;
      if ($price) $price.textContent = formatMoney(variant.price);
      const saveAmt = (variant.compare_at_price || 0) - variant.price;
      if ($compare) {
        $compare.textContent =
          saveAmt > 0 ? formatMoney(variant.compare_at_price) : "";
      }
      if ($save) {
        $save.textContent = saveAmt > 0 ? "Save " + formatMoney(saveAmt) : "";
      }

      if (dosePerDay && chewCount) {
        const days = chewCount / dosePerDay;
        let durationLabel;
        if (days < 7) {
          const d = Math.max(1, Math.round(days));
          durationLabel = d === 1 ? "~1 day" : `~${d} days`;
        } else {
          const weeks = Math.round(days / 7);
          durationLabel = weeks === 1 ? "~1 week" : `~${weeks} weeks`;
        }
        if ($dose) {
          $dose.textContent = `${formatChews(dosePerDay)} per day  ·  for dogs ${WEIGHT_LABEL[this.selectedWeight]}`;
        }
        if ($duration) $duration.textContent = `${durationLabel} at this dose`;
        card.classList.add("is-ready");
      } else {
        if ($dose) $dose.textContent = "Select your dog's weight";
        if ($duration) $duration.textContent = "Select your dog's weight";
        card.classList.remove("is-ready");
      }
    }
  }

  customElements.define("jounce-two-step", JounceTwoStep);
})();
